import { Component, computed, inject, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { catchError, finalize, of } from 'rxjs';
import { ApiService } from '../../../../../core/services/api.service';
import {
  DashboardLayoutService,
  DashboardLoadResponse,
} from '../../../../../core/services/dashboard-layout.service';
import { ToasterService } from '../../../../../core/services/toaster.service';
import {
  CustomizeDashboardStateComponent,
  CustomizeDashboardPreviewTile,
} from './customize-dashboard-state.component';
import { IconComponentV2 } from '../../../../../shared/components/icon-component/icon.component';
import {
  ConfirmationModalComponent,
  ConfirmationModalData,
} from '../../../../../shared/components/confirmation-modal/confirmation-modal.component';


export interface CustomizeDashboardStateDialogApplyResult {
  tiles: CustomizeDashboardPreviewTile[];
  mixed: boolean;
}
export type CustomizeDashboardStateDialogResult =
  | CustomizeDashboardStateDialogApplyResult
  | CustomizeDashboardPreviewTile[]
  | null;


export interface CustomizeDashboardStateDialogData {
  tiles?: CustomizeDashboardPreviewTile[] | null;

  defaultTiles?: CustomizeDashboardPreviewTile[] | null;

  mixed?: boolean;
}

@Component({
  standalone: true,
  selector: 'app-customize-dashboard-state-dialog',
  imports: [CommonModule, MatDialogModule, CustomizeDashboardStateComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="relative overflow-hidden bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-2xl rounded-2xl flex flex-col max-h-[90vh] ">

      <!-- Top Decorative Gradient Glow -->
      <div class="absolute top-0 left-1/4 right-1/4 h-24 bg-gradient-to-r from-[#AD46FF]/20 to-blue-500/20 dark:from-purple-500/30 dark:to-blue-500/30 rounded-full blur-3xl pointer-events-none"></div>

      <!-- Header -->
      <div class="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex flex-wrap items-center justify-between gap-3 bg-gradient-to-r from-[#AD46FF]/5 via-white to-[#AD46FF]/5 dark:from-purple-500/10 dark:via-slate-900 dark:to-purple-500/10 relative z-10">
        <div class="flex items-center gap-3 min-w-0">
          <div
            class="w-11 h-11 rounded-xl bg-gradient-to-br from-[#AD46FF] to-[#7c3aed] flex items-center justify-center shadow-lg shadow-[#AD46FF]/30 shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white"
              stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <rect width="7" height="9" x="3" y="3" rx="1" />
              <rect width="7" height="5" x="14" y="3" rx="1" />
              <rect width="7" height="9" x="14" y="12" rx="1" />
              <rect width="7" height="5" x="3" y="16" rx="1" />
            </svg>
          </div>
          <div class="min-w-0">
            <h2 class="text-lg sm:text-xl font-semibold text-[#141437] dark:text-white truncate">
              {{ title }}
            </h2>
            <p class="text-[12px] text-violet-700 dark:text-violet-300">
              {{ description }}
            </p>
          </div>
        </div>
        <div class="flex items-center gap-2.5 flex-wrap shrink-0 justify-end">
          <span
            class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            {{ previewSectionCount() }} sections preview
          </span>
          <button
            type="button"
            (click)="onAutoAdjustWidths()"
            [disabled]="!canAutoAdjustPreviewWidths()"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
            [ngClass]="canAutoAdjustPreviewWidths()
              ? 'bg-white dark:bg-slate-800 text-[#AD46FF] border border-[#AD46FF]/30 hover:bg-violet-50 dark:hover:bg-white/5 hover:border-[#AD46FF]/60 shadow-sm cursor-pointer'
              : 'bg-slate-50 dark:bg-slate-800 text-slate-400 dark:text-slate-500 border border-slate-200 dark:border-slate-700 cursor-not-allowed'"
            title="Expand the smallest card in each row to fill the leftover columns">
            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 3 21 3 21 9" />
              <polyline points="9 21 3 21 3 15" />
              <line x1="21" y1="3" x2="14" y2="10" />
              <line x1="3" y1="21" x2="10" y2="14" />
            </svg>
            Auto Adjust Width
          </button>
          <!-- remove all widgets button -->
          <button
              type="button"
              (click)="removeWidgets()"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all bg-white dark:bg-slate-800 text-[#AD46FF] border border-[#AD46FF]/30 hover:bg-violet-50 dark:hover:bg-white/5 hover:border-[#AD46FF]/60 shadow-sm cursor-pointer" >
            Remove All Widgets
            </button>
          <button
            (click)="onClose()"
            class="p-2 rounded-full hover:bg-slate-200/50 dark:hover:bg-white/10 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors shrink-0"
            type="button"
            aria-label="Close"
          >
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Content: the existing customize-state preview component -->
      <mat-dialog-content class="flex-1 overflow-y-auto !m-0 !p-0 relative z-10 custom-scrollbar">
        <app-customize-dashboard-state
          #preview
          [apiTiles]="dialogTiles"
          [defaultTiles]="dialogDefaultTiles"
          (applyRequested)="onApply()">
        </app-customize-dashboard-state>
      </mat-dialog-content>

      <!-- Footer — Reset (left) + Cancel/Apply (right) — mirrors MR1 popup's footer. -->
      <div
        class="flex items-center justify-between gap-3 px-5 sm:px-8 py-4 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 relative z-10">

        <!-- Reset widths — restores every tile to its default arrangement. -->
        <button
          type="button"
          (click)="onReset()"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-medium hover:text-[#AD46FF] hover:border-[#AD46FF]/40 hover:bg-violet-50 dark:hover:bg-white/5 transition-colors whitespace-nowrap"
          title="Reset all widths to default">
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
            <path d="M3 3v5h5" />
          </svg>
          Reset
        </button>

        <div class="flex items-center gap-3">
          <button
            type="button"
            (click)="onCancel()"
            class="px-5 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-slate-800 dark:hover:text-white transition-colors whitespace-nowrap">
            Cancel
          </button>
          <button
            type="button"
            (click)="onApply()"
            class="px-5 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-[#AD46FF] to-[#7c3aed] hover:shadow-lg hover:shadow-[#AD46FF]/30 hover:-translate-y-px transition-all whitespace-nowrap  disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0">
            Apply
          </button>
          <!-- [disabled]="previewSectionCount === 0" -->
        </div>
      </div>
    </div>
  `,
})
export class CustomizeDashboardStateDialogComponent {
  private dialogRef = inject(MatDialogRef<CustomizeDashboardStateDialogComponent, CustomizeDashboardStateDialogResult>);

  /** Optional dialog data (tiles to seed the preview with). May be undefined
   *  when the dialog is opened without `data: { … }` (back-compat). */
  private readonly data = inject<CustomizeDashboardStateDialogData | null>(
    MAT_DIALOG_DATA,
    { optional: true },
  );

  private readonly apiService = inject(ApiService);
  private readonly layoutService = inject(DashboardLayoutService);
  private readonly toaster = inject(ToasterService);
  private readonly dialog = inject(MatDialog);
  private saving = false;
  readonly dialogTiles: CustomizeDashboardPreviewTile[] | null =
    this.data?.tiles && this.data.tiles.length > 0
      ? this.data.tiles.map((t) => ({ ...t }))
      : null;

  readonly dialogDefaultTiles: CustomizeDashboardPreviewTile[] | null =
    this.data?.defaultTiles && this.data.defaultTiles.length > 0
      ? this.data.defaultTiles.map((t) => ({ ...t }))
      : null;


  readonly preview = viewChild<CustomizeDashboardStateComponent>('preview');

  /** Toolbar binding — total tiles currently in the preview. */
  readonly previewSectionCount = computed(
    () => this.preview()?.previewTiles().length ?? 0,
  );

  /** Toolbar binding — whether the "Auto Adjust" button has work to do. */
  readonly canAutoAdjustPreviewWidths = computed(
    () => this.preview()?.canAutoAdjustWidths() ?? false,
  );

  onAutoAdjustWidths(): void {
    this.preview()?.autoAdjustWidths();
  }

  readonly title = 'Build your dashboard';
  readonly description =
    'Preview a combined for Customize layout below, then add sections to get started.';

  mixedEnabled: boolean = this.data?.mixed ?? true;

  removeWidgets(): void {
    const ref = this.dialog.open<
      ConfirmationModalComponent,
      ConfirmationModalData,
      boolean
    >(ConfirmationModalComponent, {
      width: '400px',
      data: {
        title: 'Delete Custom Dashboard',
        description:
          'Are you sure you want to delete your customize dashboard',
        confirmLabel: 'Yes',
        cancelLabel: 'No',
      },
    });

    ref.afterClosed().subscribe((confirmed) => {
      if (confirmed !== true) return;

      const hiddenTiles: CustomizeDashboardPreviewTile[] = (
        this.preview()?.previewTiles() ?? []
      ).map((t) => ({ ...t, visible: false }));

      this.dialogRef.close({ tiles: hiddenTiles, mixed: this.mixedEnabled });
    });
  }

  /** Top-right "X" — same as Cancel. */
  onClose(): void {
    this.dialogRef.close(null);
  }

  /** Footer Cancel — dismiss without applying changes. */
  onCancel(): void {
    this.dialogRef.close(null);
  }

  /** Footer Reset — restores every tile in the preview to its default width. */
  onReset(): void {
    this.preview()?.resetWidths();
  }

  onApply(): void {
    if (this.saving) return;

    const tiles: CustomizeDashboardPreviewTile[] =
      this.preview()?.previewTiles().map((t) => ({ ...t })) ?? [];

    // Mixed = ON → legacy path, no extra API call.
    if (this.mixedEnabled) {
      this.dialogRef.close({ tiles, mixed: true });
      return;
    }

    // Mixed = OFF → hide every widget on the custom dashboard.
    this.applyMixedOffAndClose(tiles);
  }

  private resolveProductIdForLookup(): string | number | null {
    const pid = this.apiService.selectedProductPid();
    if (pid != null && /^\d+$/.test(String(pid))) return pid;
    const cuid = this.apiService.selectedProductId();
    if (cuid != null && String(cuid).length > 0) return cuid;
    return null;
  }

  private applyMixedOffAndClose(tiles: CustomizeDashboardPreviewTile[]): void {
    const closeResult = { tiles, mixed: true } as const;

    const productId = this.resolveProductIdForLookup();
    if (productId == null) {
      this.dialogRef.close(closeResult);
      return;
    }

    this.saving = true;
    this.apiService
      .get<DashboardLoadResponse>(
        '/dashboards',
        { platform: 'web', mixed: true, product_id: productId },
        { showLoader: false, showErrorToaster: true },
      )
      .pipe(
        catchError((err) => {
          return of(null as DashboardLoadResponse | null);
        }),
      )
      .subscribe((dashRes) => {
        const dashboardId = dashRes?.dashboard?.id;
        const widgets = Array.isArray(dashRes?.widgets) ? dashRes!.widgets : [];

        const numericDashboardId =
          dashboardId != null && /^\d+$/.test(String(dashboardId))
            ? Number(dashboardId)
            : null;

        if (numericDashboardId == null || widgets.length === 0) {
          this.saving = false;
          this.dialogRef.close(closeResult);
          return;
        }

        const batchWidgets = widgets
          .map((w) => {
            const widgetId = w?.widget_id;
            const sourcePageId = w?.source_page?.id ?? null;
            if (widgetId == null) return null;
            return {
              widget_id: widgetId,
              source_page_id: sourcePageId,
              is_visible: false,
            };
          })
          .filter((entry): entry is {
            widget_id: number | string;
            source_page_id: number | string | null;
            is_visible: false;
          } => entry !== null);

        if (batchWidgets.length === 0) {
          this.saving = false;
          this.dialogRef.close(closeResult);
          return;
        }
        this.layoutService
          .batchUpdateWidgets(numericDashboardId, { widgets: batchWidgets })
          .pipe(
            catchError((err) => {
              this.toaster.error(
                err?.error?.message ||
                'Failed to disable mixed mode. Please try again.',
              );
              return of(null);
            }),
            finalize(() => {
              this.saving = false;
            }),
          )
          .subscribe(() => {
            this.dialogRef.close(closeResult);
          });
      });
  }
}
